/**
 * Service layer — Time & Expense Records (the admin record browser). See docs/TIME_RECORDS.md.
 *
 * Distinct from services/timecards.js on purpose: that file is the data seam for the two WORKFLOW
 * tools (staff Time Card, manager Time Review), which are faithful Softr ports consuming an
 * Airtable-shaped record. This browser is new code with no port to stay faithful to, so it reads
 * the Supabase row shape directly — flat, typed, and with the Core-lock fields first-class.
 *
 * All calls go through the Clerk-authed api/time-entries.js. The lock itself is enforced
 * server-side; the helpers here just expose it to the UI so a locked row renders read-only rather
 * than failing on submit.
 */

async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

async function recPost(body) {
  const token = await authToken()
  const res = await fetch('/api/time-entries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function recGet(qs) {
  const data = await recGetRaw(qs)
  return data.rows || []
}
/** Like recGet, but returns the raw response body instead of assuming a `rows[]` shape. */
async function recGetRaw(qs) {
  const token = await authToken()
  const res = await fetch('/api/time-entries?' + qs, { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  let data = {}; try { data = await res.json() } catch { data = {} }
  if (!res.ok) throw new Error(data.error || ('Request failed: ' + res.status))
  return data
}

/* ── The lock, mirrored client-side ──────────────────────────────────────────────────────────────
 * Same rule as api/time-entries.js#isInCore. Duplicated deliberately (not imported — one is server
 * CommonJS-ish ESM in api/, the other browser code) and kept to a single expression so the two can't
 * drift in a way that matters: the server is the enforcer, this is only for rendering.
 */
export function isInCore(row) {
  return !!row && (row.sent_to_core === true || !!row.core_entry_id)
}

/** What this row still allows, so the UI never offers an action the server will refuse. */
export function rowCapabilities(row) {
  const inCore = isInCore(row)
  const status = (row && row.status) || ''
  return {
    inCore,
    editFinancial: !inCore,                                          // hours / memo / billable / phase / activity
    editNotes: true,                                                 // review notes + entity attribution, always
    canDelete: !inCore,
    canApprove: !inCore && status !== 'Approved',
    canReject: !inCore && status !== 'Rejected',
    canPost: !inCore && (status === 'Approved' || status === 'Post Failed'),
    canFlagCorrection: inCore,                                       // the only correction path once Core owns it
    // Offered on every in-Core row — whether it's ACTUALLY still revisable (not yet billed on an
    // invoice) is a live fact only Core knows, so the real gate happens when Revise is clicked
    // (getCoreBillStatus) and again server-side on save. This flag just controls whether the button
    // renders at all.
    canRevise: inCore,
  }
}

/**
 * Live check: has BQE Core attached this entry to an invoice yet? Call this right before opening
 * the Revise form — `sent_to_core`/`core_entry_id` only prove the entry reached Core, not that it's
 * still unbilled. The server repeats this same check again on save (never trust it twice-removed).
 */
export function getCoreBillStatus(id) {
  return recGetRaw('action=coreBillStatus&id=' + encodeURIComponent(id))
}

/**
 * Deep link to a posted entry's own record inside BQE Core — read-only reference, never a write
 * path (see docs/TIME_RECORDS.md's Core lock). Pattern confirmed 2026-08 against a real Core URL
 * (Matt pasted one from his browser address bar): the id is the same GUID this app already stores
 * in `core_entry_id` on every posted entry, so no new capture was needed — just the link.
 */
export function coreEntryUrl(coreEntryId) {
  return coreEntryId ? 'https://wap1-01.bqecore.com/webapp/TimeEntry/TimeEntryEdit/' + encodeURIComponent(coreEntryId) : null
}

/** Why an entry hasn't reached Core — drives the reconciliation strip's breakdown. */
export function notInCoreReason(row) {
  if (isInCore(row)) return null
  switch ((row && row.status) || '') {
    case 'Pending Review': return 'Draft, never submitted'
    case 'Submitted':      return 'Awaiting review'
    case 'Rejected':       return 'Sent back, not fixed'
    case 'Approved':       return 'Approved, never posted'
    case 'Post Failed':    return 'Post to Core failed'
    default:               return 'Unknown state'
  }
}

/**
 * The browser's one read. Every filter optional; `notInCore` defaults ON at the call site because
 * the question this tool exists to answer is "what did we log that Core never received."
 */
export function getTimeRecords({ projectUid, memberId, from, to, statuses, notInCore, needsCorrection } = {}) {
  const p = new URLSearchParams({ action: 'records' })
  if (projectUid) p.set('projectUid', projectUid)
  if (memberId) p.set('memberId', memberId)
  if (from) p.set('from', from)
  if (to) p.set('to', to)
  if (statuses && statuses.length) p.set('statuses', statuses.join(','))
  if (notInCore) p.set('notInCore', '1')
  if (needsCorrection) p.set('needsCorrection', '1')
  return recGet(p.toString())
}

/** Projects that actually have time logged against them (not just currently-active ones). */
export function getRecordProjects() {
  return recGet('action=recordProjects')
}

export function getEntryAudit(id) {
  return recGet('action=audit&id=' + encodeURIComponent(id))
}

export function saveRecordEdit(id, patch) {
  return recPost({ action: 'saveEdit', id, ...patch })
}
export function setRecordStatus(id, status) {
  return recPost({ action: 'setStatus', id, status })
}
export function deleteRecord(id) {
  return recPost({ action: 'delete', id })
}
export function flagRecordCorrection(id, note) {
  return recPost({ action: 'flagCorrection', id, note })
}
export function clearRecordCorrection(id) {
  return recPost({ action: 'clearCorrection', id })
}

/**
 * Revise an already-Posted entry — the one path that can still change hours/memo/billable/phase/
 * activity/date on an in-Core row. The server re-checks BQE Core live (has this been billed on an
 * invoice yet?) and refuses with `{error, locked:true, billed:true, invoiceNumber}` if so; on
 * success it has already patched BOTH Core and Cortex. `patch` accepts any of
 * { hours, clientHours, memo, billable, phaseId, activityId, entryDate }.
 */
export function reviseRecord(id, patch) {
  return recPost({ action: 'revisePosted', id, ...patch })
}

/**
 * Post (or retry) one entry to BQE Core. Reuses the existing endpoint unchanged — it independently
 * refuses anything that isn't Approved or Post Failed, so this button can't double-post even if the
 * client-side capability check were wrong.
 */
export async function postRecordToCore(id) {
  const token = await authToken()
  const res = await fetch('/api/post-time-entry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ timeEntryId: id }),
  })
  let data = {}; try { data = await res.json() } catch { data = {} }
  return data
}
